// Entity API service for raid entity management
import { supabase } from '../lib/supabase';
import { RaidEntity, EntityType, RaidEntityProperty } from '../types';

// Entity API
export const entityApi = {
  // 모든 엔티티 조회
  async getAll(): Promise<RaidEntity[]> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .order('entity_type', { ascending: true })
      .order('entity_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 타입별 엔티티 조회
  async getByType(entityType: EntityType): Promise<RaidEntity[]> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .eq('entity_type', entityType)
      .order('entity_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 보스만 조회 (더블보스, 페이즈보스 포함)
  async getBosses(): Promise<RaidEntity[]> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .in('entity_type', ['BOSS', 'BOSS_DOUBLE', 'BOSS_PHASE'])
      .order('entity_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 소환수 조회
  async getSummons(parentCode?: string): Promise<RaidEntity[]> {
    let query = supabase
      .from('raid_entity_renewed')
      .select('*')
      .eq('entity_type', 'SUMMON');
    
    if (parentCode) {
      query = query.eq('parent_code', parentCode);
    }
    
    const { data, error } = await query.order('entity_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 특정 엔티티 조회
  async getByCode(entityCode: string): Promise<RaidEntity | null> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .eq('entity_code', entityCode)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // 엔티티 생성
  async create(entity: Omit<RaidEntity, 'id' | 'created_at' | 'updated_at'>): Promise<RaidEntity> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .insert(entity)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 엔티티 수정
  async update(entityCode: string, updates: Partial<RaidEntity>): Promise<RaidEntity> {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .update(updates)
      .eq('entity_code', entityCode)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 엔티티 삭제
  async delete(entityCode: string): Promise<void> {
    const { error } = await supabase
      .from('raid_entity_renewed')
      .delete()
      .eq('entity_code', entityCode);
    
    if (error) throw error;
  },

  // 엔티티 속성 조회
  async getProperties(entityCode: string): Promise<RaidEntityProperty[]> {
    const { data, error } = await supabase
      .from('raid_entity_property')
      .select('*')
      .eq('entity_code', entityCode)
      .order('property_key', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 엔티티 속성 설정
  async setProperty(
    entityCode: string, 
    propertyKey: string, 
    propertyValue: string, 
    propertyType: RaidEntityProperty['property_type'] = 'STRING'
  ): Promise<RaidEntityProperty> {
    const { data, error } = await supabase
      .from('raid_entity_property')
      .upsert({
        entity_code: entityCode,
        property_key: propertyKey,
        property_value: propertyValue,
        property_type: propertyType
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 엔티티와 관련된 모든 데이터 조회
  async getWithRelations(entityCode: string): Promise<{
    entity: RaidEntity | null;
    properties: RaidEntityProperty[];
    children: RaidEntity[];
  }> {
    const [entity, properties, children] = await Promise.all([
      this.getByCode(entityCode),
      this.getProperties(entityCode),
      this.getSummons(entityCode)
    ]);

    return { entity, properties, children };
  }
};